/* =============================================================================
   Handschrift speichern, laden, im Browser einsetzbar machen

   Firestore, kein Cloud Storage: Storage verlangt seit Februar 2026 ein
   hinterlegtes Rechnungskonto, Firestore nicht. Das passt hier gut, denn eine
   Handschrift ist klein — die bestehenden Kundenschriften liegen zwischen 11
   und 21 KB, das Limit eines Firestore-Dokuments sind 1 MiB.

   Zwei Dokumente je Nutzer:
     users/{uid}/handschrift/striche  — die Zeichnung selbst (gzip-JSON)
     users/{uid}/handschrift/font     — die daraus gebaute Schriftdatei

   Die Striche werden mitgespeichert, obwohl zum Schreiben nur die Schriftdatei
   nötig wäre. Nur so kann jemand später einzelne Buchstaben nachbessern, ohne
   alles neu zu zeichnen — und nur so lässt sich die Schrift neu bauen, wenn
   die Umwandlung später besser wird.
   ========================================================================== */

import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, Bytes } from "firebase/firestore";
import { app, db } from "../firebase";

const FIRESTORE_MAX = 1048487; // grösster erlaubter Feldwert in Bytes
const CACHE_DB = "schriftbot-handschrift";
const CACHE_STORE = "fonts";

/* --- gzip über die Browser-eigenen Streams, mit Rückfallebene ------------- */

async function packe(text) {
  const roh = new TextEncoder().encode(text);
  if (typeof CompressionStream === "undefined") return { bytes: roh, gezippt: false };
  try {
    const stream = new Blob([roh]).stream().pipeThrough(new CompressionStream("gzip"));
    const buf = await new Response(stream).arrayBuffer();
    return { bytes: new Uint8Array(buf), gezippt: true };
  } catch {
    return { bytes: roh, gezippt: false };
  }
}

async function entpacke(bytes, gezippt) {
  if (!gezippt) return new TextDecoder().decode(bytes);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  const buf = await new Response(stream).arrayBuffer();
  return new TextDecoder().decode(buf);
}

/* --- IndexedDB als Zwischenspeicher ---------------------------------------
   Damit die eigene Schrift beim zweiten Besuch sofort steht und nicht auf
   Firestore gewartet werden muss. Rein lokal; schlägt es fehl, wird einfach
   wieder aus Firestore geladen.                                             */

function oeffneCache() {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(CACHE_DB, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(CACHE_STORE)) {
          req.result.createObjectStore(CACHE_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function cacheLies(uid) {
  const idb = await oeffneCache();
  if (!idb) return null;
  return new Promise((resolve) => {
    try {
      const req = idb.transaction(CACHE_STORE, "readonly").objectStore(CACHE_STORE).get(uid);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function cacheSchreib(uid, eintrag) {
  const idb = await oeffneCache();
  if (!idb) return;
  try {
    idb.transaction(CACHE_STORE, "readwrite").objectStore(CACHE_STORE).put(eintrag, uid);
  } catch {
    /* Zwischenspeicher ist Beiwerk, kein Grund zum Abbrechen */
  }
}

async function cacheLoesche(uid) {
  const idb = await oeffneCache();
  if (!idb) return;
  try {
    idb.transaction(CACHE_STORE, "readwrite").objectStore(CACHE_STORE).delete(uid);
  } catch {
    /* egal */
  }
}

/* --- Schrift im Browser verfügbar machen --------------------------------- */

const registriert = new Map(); // uid -> FontFace

function alsBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let s = "";
  const schritt = 0x8000; // in Häppchen, sonst sprengt es den Aufrufstapel
  for (let i = 0; i < bytes.length; i += schritt) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + schritt));
  }
  return btoa(s);
}

/* Dieselbe Schrift zusätzlich als CSS-Regel hinterlegen.

   Nötig für den Bild- und PDF-Export: html2canvas baut die Seite zum
   Abfotografieren in einem eigenen Rahmen nach und übernimmt dorthin die
   Stylesheets. Eine Schrift, die nur über die FontFace-Schnittstelle in
   JavaScript angemeldet ist, kennt dieser Rahmen nicht — der Export fiel
   deshalb auf eine Ersatzschrift zurück, mit deren Laufweiten die Buchstaben
   ineinanderliefen. Als CSS-Regel mit eingebetteter Schrift wird sie
   mitkopiert. */
function alsStylesheet(uid, buffer) {
  const id = "sb-schrift-" + uid;
  const alt = document.getElementById(id);
  if (alt) alt.remove();
  try {
    const style = document.createElement("style");
    style.id = id;
    style.textContent =
      `@font-face{font-family:"${uid}";` +
      `src:url(data:font/otf;base64,${alsBase64(buffer)}) format("opentype");` +
      `font-weight:normal;font-style:normal;font-display:block;}`;
    document.head.appendChild(style);
  } catch (err) {
    console.warn("Schrift konnte nicht als Stylesheet hinterlegt werden:", err);
  }
}

export async function registriereFont(uid, buffer) {
  const alt = registriert.get(uid);
  if (alt) {
    try {
      document.fonts.delete(alt);
    } catch {
      /* war schon weg */
    }
  }
  // Kopie übergeben: FontFace übernimmt den Puffer, ein späterer Zugriff
  // auf denselben ArrayBuffer würde sonst ins Leere greifen.
  const face = new FontFace(uid, buffer.slice(0));
  await face.load();
  document.fonts.add(face);
  registriert.set(uid, face);

  // Zweiter Weg für den Export, siehe oben
  alsStylesheet(uid, buffer);
  return face;
}

export function istRegistriert(uid) {
  return registriert.has(uid);
}

export function entferneFont(uid) {
  const face = registriert.get(uid);
  if (face) {
    try {
      document.fonts.delete(face);
    } catch {
      /* egal */
    }
    registriert.delete(uid);
  }
  const style = document.getElementById("sb-schrift-" + uid);
  if (style) style.remove();
}

/* --- Speichern ------------------------------------------------------------ */

export async function speichereHandschrift(uid, striche, fontBuffer) {
  const json = JSON.stringify(striche);
  const { bytes: stricheBytes, gezippt } = await packe(json);
  const fontBytes = new Uint8Array(fontBuffer);

  if (stricheBytes.byteLength > FIRESTORE_MAX) {
    throw new Error(
      "Die Zeichnung ist zu umfangreich zum Speichern. Bitte zeichne die Buchstaben etwas zügiger — sehr langsame Striche erzeugen besonders viele Messpunkte."
    );
  }
  if (fontBytes.byteLength > FIRESTORE_MAX) {
    throw new Error("Die erzeugte Schrift ist zu gross zum Speichern.");
  }

  const anzahlZeichen = Object.keys(striche.glyphen || {}).length;

  await setDoc(doc(db, "users", uid, "handschrift", "striche"), {
    version: 1,
    gezippt,
    anzahlZeichen,
    data: Bytes.fromUint8Array(stricheBytes),
    aktualisiert: serverTimestamp(),
  });

  await setDoc(doc(db, "users", uid, "handschrift", "font"), {
    version: 1,
    anzahlZeichen,
    format: "otf",
    data: Bytes.fromUint8Array(fontBytes),
    aktualisiert: serverTimestamp(),
  });

  await registriereFont(uid, fontBytes.buffer);
  await cacheSchreib(uid, { font: fontBytes, anzahlZeichen, gespeichert: Date.now() });

  return { stricheBytes: stricheBytes.byteLength, fontBytes: fontBytes.byteLength, anzahlZeichen };
}

/* --- Laden ---------------------------------------------------------------- */

/* Google liefert Bytes-Felder mal in gewöhnlichem, mal in URL-sicherem
   Base64. atob kennt nur die gewöhnliche Variante und wirft bei "-" und "_",
   deshalb vorher umschreiben und die Auffüllung ergänzen. */
function base64FeldZuBytes(b64) {
  const sauber = b64.replace(/-/g, "+").replace(/_/g, "/");
  const gefuellt = sauber + "=".repeat((4 - (sauber.length % 4)) % 4);
  const roh = atob(gefuellt);
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

/* Ein einzelnes Dokument über die Firestore-REST-Schnittstelle holen.

   Warum nicht einfach getDoc: das SDK baut für seinen ersten Zugriff einen
   Datenstrom zu Firestore auf (WebChannel, mit Rückfall auf Long Polling, das
   der Client erst aushandeln muss). Auf dem Rechner fällt das kaum auf, auf dem
   Telefon dauerte genau das sehr lange — gemeldet wurden rund 30 Sekunden für
   eine Schrift mit zwei Zeichen. An der Datenmenge liegt es also nicht: eine
   Handschrift ist rund 20 KB. Und weil es am Verbindungsaufbau liegt statt an
   der Grösse, hilft auch ein zweiter Aufruf im selben Gerät nichts — mal ist
   der Kanal gerade warm, mal nicht, unabhängig davon, ob es der erste oder
   zweite Besuch ist.

   Für einen einzelnen Lesezugriff braucht es diesen Apparat nicht. Ein
   gewöhnliches GET holt dasselbe Dokument in einem Rutsch. Es gelten dieselben
   Sicherheitsregeln — der Zugriff läuft mit demselben Anmeldezeichen wie im
   SDK, nur eben über eine schlichte HTTPS-Anfrage.

   Das Zeitlimit deckt auch den Token-Abruf mit ab, nicht nur den fetch selbst
   — sonst könnte ein hängendes getIdToken() die Frist unbemerkt umgehen.

   Gibt das rohe Firestore-REST-Dokument zurück, null bei "gibt es nicht", und
   wirft bei allem anderen — dann übernimmt der Aufrufer das SDK. */
async function holeDokumentUeberRest(pfad, zeitlimitMs = 8000) {
  const abbruch = new AbortController();
  const uhr = setTimeout(() => abbruch.abort(), zeitlimitMs);
  const abgebrochen = new Promise((_, reject) => {
    abbruch.signal.addEventListener("abort", () =>
      reject(new Error("Zeitlimit überschritten"))
    );
  });
  try {
    const { getAuth } = await import("firebase/auth");
    const nutzer = getAuth(app).currentUser;
    if (!nutzer) throw new Error("nicht angemeldet");

    const token = await Promise.race([nutzer.getIdToken(), abgebrochen]);
    const projekt = app.options.projectId;
    const url =
      `https://firestore.googleapis.com/v1/projects/${projekt}` +
      `/databases/(default)/documents/${pfad}`;

    const antwort = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abbruch.signal,
    });
    if (antwort.status === 404) return null; // Dokument gibt es nicht
    if (!antwort.ok) throw new Error(`REST ${antwort.status}`);
    return await antwort.json();
  } finally {
    clearTimeout(uhr);
  }
}

async function holeFontUeberRest(uid, zeitlimitMs = 8000) {
  const pfad = `users/${encodeURIComponent(uid)}/handschrift/font`;
  const doc = await holeDokumentUeberRest(pfad, zeitlimitMs);
  if (!doc) return null;
  const b64 = doc.fields && doc.fields.data && doc.fields.data.bytesValue;
  if (!b64) return null;
  const anzahl = Number(
    (doc.fields.anzahlZeichen && doc.fields.anzahlZeichen.integerValue) || 0
  );
  return { bytes: base64FeldZuBytes(b64), anzahlZeichen: anzahl };
}

/* Dieselbe Abkürzung für die Zeichnung (striche) — vorher lief das Laden im
   Handschrift-Editor ausschliesslich über getDoc und war deshalb demselben
   Verbindungsaufbau-Problem ausgesetzt wie früher die Schriftdatei: die
   Vorschau-Schrift war schon da (über den REST-Weg oben geladen), aber der
   Editor stiess beim Öffnen einen zweiten, ungeschützten SDK-Zugriff an. */
async function holeStricheUeberRest(uid, zeitlimitMs = 8000) {
  const pfad = `users/${encodeURIComponent(uid)}/handschrift/striche`;
  const doc = await holeDokumentUeberRest(pfad, zeitlimitMs);
  if (!doc) return null;
  const b64 = doc.fields && doc.fields.data && doc.fields.data.bytesValue;
  if (!b64) return null;
  const gezippt = !!(doc.fields.gezippt && doc.fields.gezippt.booleanValue);
  return { bytes: base64FeldZuBytes(b64), gezippt };
}

/* Denselben Inhalt über das SDK holen — die Rückfallebene. */
async function holeFontUeberSdk(uid) {
  const snap = await getDoc(doc(db, "users", uid, "handschrift", "font"));
  if (!snap.exists()) return null;
  const bytes = snap.data().data;
  if (!bytes) return null;
  return {
    bytes: bytes.toUint8Array(),
    anzahlZeichen: snap.data().anzahlZeichen || 0,
  };
}

/* Frische Fassung holen und ablegen. Wird auch im Hintergrund gerufen. */
async function holeUndLegeAb(uid) {
  let ergebnis = null;
  try {
    ergebnis = await holeFontUeberRest(uid);
  } catch (err) {
    // Nicht angemeldet, Netz weg, Frist abgelaufen — dann eben das SDK.
    console.warn("Handschrift über REST nicht ladbar, nutze das SDK:", err);
    ergebnis = await holeFontUeberSdk(uid);
  }
  if (!ergebnis) return null;

  await cacheSchreib(uid, {
    font: ergebnis.bytes,
    anzahlZeichen: ergebnis.anzahlZeichen,
    gespeichert: Date.now(),
  });
  return ergebnis;
}

/* Holt die Schriftdatei und macht sie einsatzbereit. Rückgabe: true, wenn die
   Schrift benutzt werden kann.

   Liegt sie im lokalen Zwischenspeicher, ist hier sofort Schluss. Vorher wurde
   auch dann noch auf Firestore gewartet, bevor die Funktion zurückkam — die
   Schrift war zwar schon angemeldet und der Text damit gesetzt, aber der
   Aufrufer in Home.js hängt an diesem Versprechen und schaltete die
   Handschrift-Bedienelemente erst danach frei. Der Abgleich läuft jetzt im
   Hintergrund weiter; eine anderswo gespeicherte neuere Fassung ist damit beim
   nächsten Aufruf da. */
export async function ladeUndRegistriereFont(uid, { nurCache = false } = {}) {
  if (!uid) return false;
  if (registriert.has(uid)) return true;

  const cache = await cacheLies(uid);
  if (cache && cache.font) {
    try {
      await registriereFont(uid, cache.font.buffer || cache.font);
      if (!nurCache) {
        // Im Hintergrund abgleichen, ohne den Aufrufer aufzuhalten.
        holeUndLegeAb(uid).catch((err) =>
          console.warn("Abgleich der Handschrift fehlgeschlagen:", err)
        );
      }
      return true;
    } catch {
      await cacheLoesche(uid);
    }
  }
  if (nurCache) return registriert.has(uid);

  try {
    const ergebnis = await holeUndLegeAb(uid);
    if (!ergebnis) return registriert.has(uid);
    await registriereFont(uid, ergebnis.bytes.buffer);
    return true;
  } catch (err) {
    console.warn("Handschrift konnte nicht geladen werden:", err);
    return registriert.has(uid);
  }
}

/* Die Zeichnung selbst — zum Nachbessern einzelner Buchstaben. Derselbe
   REST-zuerst-dann-SDK-Weg wie beim Schrift-Laden, siehe
   holeDokumentUeberRest oben. */
async function holeStricheDaten(uid) {
  try {
    return await holeStricheUeberRest(uid);
  } catch (err) {
    console.warn("Zeichnung über REST nicht ladbar, nutze das SDK:", err);
    const snap = await getDoc(doc(db, "users", uid, "handschrift", "striche"));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (!d.data) return null;
    return { bytes: d.data.toUint8Array(), gezippt: d.gezippt };
  }
}

export async function ladeStriche(uid) {
  if (!uid) return null;
  try {
    const ergebnis = await holeStricheDaten(uid);
    if (!ergebnis) return null;
    return JSON.parse(await entpacke(ergebnis.bytes, ergebnis.gezippt));
  } catch (err) {
    console.warn("Zeichnung konnte nicht geladen werden:", err);
    return null;
  }
}

export async function loescheHandschrift(uid) {
  entferneFont(uid);
  await cacheLoesche(uid);
  await Promise.all([
    deleteDoc(doc(db, "users", uid, "handschrift", "striche")).catch(() => {}),
    deleteDoc(doc(db, "users", uid, "handschrift", "font")).catch(() => {}),
  ]);
}
